 /**
 * Sulfad Buy 2 Get 1 Free UX helper
 *
 * Goal:
 * - On the Sulfad PDP: if the customer sets quantity to 2 and adds to cart,
 *   automatically bump it to 3 so they always receive the free item.
 * - In the cart drawer / cart items: if the Sulfad line is updated to quantity 2,
 *   automatically adjust the quantity to 3 before the cart change request runs.
 *
 * NOTE: The pricing / free item is still handled by Shopify discounts.
 * This script only nudges the quantity to a safe value so users don't "miss" the offer.
 */

// Update these constants if the Sulfad handle or variant id ever change.
const SULFAD_PRODUCT_HANDLE =
  'sulfad-liver-function-support-anti-oxidant-potent-hepatoprotective-anti-inflammatoryempower-metabolic-activity-30-tab';
const SULFAD_VARIANT_ID = '43173916115022';

/**
 * Creates or updates a small helper message element near a target container.
 * @param {HTMLElement} container
 * @param {string} text
 */
function showSulfadMessage(container, text) {
  if (!container) return;

  let msg = container.querySelector('.sulfad-bogo-message');
  if (!msg) {
    msg = document.createElement('p');
    msg.className = 'sulfad-bogo-message';
    msg.setAttribute('aria-live', 'polite');
    // Insert message just after the quantity selector when possible
    const quantityWrapper = container.querySelector('.quantity-selector-wrapper') || container.firstElementChild;
    if (quantityWrapper && quantityWrapper.nextSibling) {
      quantityWrapper.parentNode.insertBefore(msg, quantityWrapper.nextSibling);
    } else {
      container.appendChild(msg);
    }
  }
  msg.textContent = text;
}

/**
 * Returns true when we are on the Sulfad product page.
 * Relies on Theme.template.productHandle when available,
 * and falls back to checking the URL path.
 */
function isSulfadProductPage() {
  const handleFromTheme = typeof Theme !== 'undefined' && Theme.template && Theme.template.productHandle;
  const path = window.location.pathname || '';

  return (
    handleFromTheme === SULFAD_PRODUCT_HANDLE ||
    path.includes(`/products/${SULFAD_PRODUCT_HANDLE}`)
  );
}

/**
 * PDP logic: if user tries to add exactly 2 Sulfad units,
 * silently bump the quantity to 3 right before submit.
 */
function setupSulfadPdpGuard() {
  if (!isSulfadProductPage()) return;

  const formComponent = document.querySelector('product-form-component[data-product-id]');
  if (!formComponent) return;

  formComponent.addEventListener(
    'submit',
    (event) => {
      // Find the main quantity input inside the product form.
      /** @type {HTMLInputElement | null} */
      const qtyInput = formComponent.querySelector('quantity-selector-component input[ref="quantityInput"]');
      if (!qtyInput) return;

      const currentQty = parseInt(qtyInput.value, 10) || 0;

      // UX rule: only auto-bump when exactly 2 are selected.
      if (currentQty === 2) {
        qtyInput.value = '3';

        const buttonsContainer =
          qtyInput.closest('.product-form-buttons') ||
          qtyInput.closest('.quantity-selector-wrapper') ||
          formComponent;

        showSulfadMessage(
          buttonsContainer,
          'We updated your Sulfad quantity to 3 so your free tablet is included (Buy 2, get 1 free).'
        );
      }
    },
    { capture: true }
  );
}

/**
 * Cart drawer / cart page logic:
 * When the customer changes quantity for the Sulfad line and it ends up at 2,
 * immediately trigger one more increment so the cart updates to 3.
 * This piggybacks the existing cart quantity logic instead of custom events.
 */
function setupSulfadCartGuard() {
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof HTMLElement)) return;

      // Only care about clicks inside cart quantity controls (drawer or cart page).
      const row = event.target.closest('.cart-items__table-row');
      if (!row) return;

      const quantityControls = row.querySelector('.cart-items__quantity-controls');
      if (!quantityControls || !quantityControls.contains(event.target)) return;

      const titleEl = row.querySelector('.cart-items__title');
      const titleText = (titleEl && titleEl.textContent) || '';
      if (!titleText.toLowerCase().includes('sulfad')) return;

      // Let the theme's own click handler run first, then inspect the new value.
      setTimeout(() => {
        const qtyInput =
          row.querySelector('cart-quantity-selector-component input[ref="quantityInput"]') ||
          row.querySelector('cart-quantity-selector-component input[name="updates[]"]');

        if (!(qtyInput instanceof HTMLInputElement)) return;

        const currentQty = parseInt(qtyInput.value, 10) || 0;
        if (currentQty !== 2) return;

        // Simulate another "+" click to move from 2 → 3 using the existing cart logic.
        const plusButton = row.querySelector('.quantity-plus');
        if (plusButton instanceof HTMLButtonElement) {
          plusButton.click();

          const controlsContainer =
            qtyInput.closest('.cart-items__quantity-controls') || row.querySelector('.cart-items__quantity') || row;

          showSulfadMessage(
            controlsContainer,
            'Your Sulfad quantity was updated to 3 so your free tablet is included (Buy 2, get 1 free).'
          );
        }
      }, 0);
    },
    true
  );
}

function initSulfadBogo() {
  setupSulfadPdpGuard();
  setupSulfadCartGuard();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSulfadBogo);
} else {
  initSulfadBogo();
}

